import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';

import {
  OrderType,
  OrderWithContext,
  OrderMeal,
  PromotionsApiError,
  PromotionsApiErrorCode,
  ErrorMode,
  Promotion,
  OrderApiException,
  OrderErrorCode,
  Order,
  NonFoodItem,
  MenuNonFoodItem,
  CustomerProfile,
  BagContent,
} from '@/models';
import { ButtonType } from '../CmgButton/cmg-button';
import { getGroupOrderParticipantCount } from '@/services/order/order-helper';
import { Routes } from '@/router';
import { sortFn, makeFormField, any, joinWithSuffix, formatOrderDate, getCountryCodeByTopLevelDomain, isMealBYOProtein, orderContainsBYOProtein, sortMenuNonFoodItems } from '@/helpers';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { BagClickEvent, BagPageLoadEvent } from '../../services/analytics/events';
import { BYOFM_BOWLS_MENU_ID, BYOFM_SERVING_UTENSILS_MENU_ID, NAPKINS_AND_UTENSILS_MENU_ID, countries } from '@/constants';
import { AccountManagementEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';
import { COUNTRIES_TAX_INCLUSIVE } from '@/store/ui';

interface StylesDescriptor {
  empty: boolean;
}

// Formerly set to the point value of free entree to reduce API calls to SessionM. Now caching is done on API side to help with load on SessionM.
// No longer need to hardcode point values here, but a future optimization to reduce calls to RewardStore Api can be done by
// looking at the reward store offers data available in Vuex to get the dynamic min point value of all current bag offers.
const CONTEXTUAL_BAG_POINTS_MINIMUM = 1;

@Component({
  name: 'CmgBag',
})
export default class CmgBag extends Vue {
  @Prop({ default: 'bagCheckout', type: String })
  bagCheckout?: string;
  @Prop({ default: 'signinRewards', type: String })
  signinRewards?: string;
  mealBeingEdited: OrderMeal | null = null;
  editMealRepositionHandler?: () => void;

  promoCode = makeFormField();
  promoLoading = false;
  rewardLoading = false;
  promoBeingRemoved: Promotion | null = null;
  contextualRewardUsed = false;
  bagContent: BagContent | null = null;
  startY = 0; // for mobile device scrolling
  bagBodyScrollTop = 0; // for mobile device scrolling
  displayedNonFoodItems = [] as{ item: MenuNonFoodItem; checked: boolean }[];
  // This is essentially the same as full page loading, but without the pepper icon
  shouldShowOverlay = false;

  get promoErrorMessage(): string {
    return this.$appStore.state.order.promoErrorMessage;
  }

  get promoPlaceholder(): string {
    return this.bagContent?.promoCodeText || 'Enter a Promo Code';
  }

  get medallionReward(): string {
    return this.$appStore.state.cms.images.logos.medallionReward.default;
  }

  get medallionFlutedReward(): string{
    return this.$appStore.state.cms.images.logos.medallionFluted.default;
  }

  get isFullPageLoading(): boolean {
    return this.$appStore.state.ui.loaders.bag > 0;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.bag.visible;
  }
  
  get styles(): object {
    const descriptor = this.getStylesDescriptor();

    return {
      empty: descriptor.empty,
      'has-meals': !descriptor.empty,
      'editing-meal': this.mealBeingEdited != null,
      'group-participant': this.isGroupOrder && !this.isGroupOrderHost,
    };
  }

  get currentOrder(): OrderWithContext | null {
    return this.$store.getters.currentOrder;
  }

  get orderDiscounts(): Promotion[] {
    return this.currentOrder?.discounts || [];
  }

  get discountExpirationDate(): string {
    if (this.currentOrder && this.currentOrder.discounts) {
      return formatOrderDate(this.currentOrder.discounts[0].expirationDate, getCountryCodeByTopLevelDomain())
    }
      return '';
  }

  get hasMeals() {
    return (this.currentOrder?.order.meals || []).length;
  }

  get hasDiscounts() {
    return (this.currentOrder?.discounts || []).length;
  }

  get discountErrors() {
    return this.currentOrder?.order.discountErrorCode;
  }

  get discountErrorsContent() {
    return this.currentOrder?.order.discountErrorContent;
  }

  get totals(): Order | undefined {
    if (!this.currentOrder) {
      return;
    }

    this.getBagOffers();
    return this.currentOrder.order;
  }

  get orderMealsPrice() {
    if (this.totals) {
      const taxInclusiveIndex = COUNTRIES_TAX_INCLUSIVE.findIndex(item => new RegExp(this.$appStore.state.ui.countryProfile.countryCode, 'i').test(item));
      return taxInclusiveIndex >= 0 ? this.formatTotal(this.totals.orderTotalAmount + this.totals.orderDiscountAmount) : this.formatTotal(this.totals.orderMealsExtendedPrice);
    }

    return 0;
  }

  get bagTotal(): number {
    if (!this.currentOrder) {
      return 0;
    }

    const tip = this.currentOrder.order.orderTipAmount;
    const donation = this.currentOrder.order.orderDonationAmount;

    return this.currentOrder.order.orderTotalAmount - tip - donation;
  }

  get closeButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.xDarkBrown.default;
  }

  get pepperFillImageUrl(): string {
    return this.$appStore.state.cms.images.logos.pepperFill.default;
  }

  get addPersonImageUrl(): string {
    return this.$appStore.state.cms.images.icons.addPerson.default;
  }

  get removePromoImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.closeSmall.default;
  }

  get defaultReward(): string {
    return this.$appStore.state.cms.images.logos.medallionRewardShout.default;
  }

  get shouldShowGroupInvite(): boolean {
    return this.currentOrder == null || this.currentOrder.order.orderType != OrderType.Group;
  }

  get isGroupOrderHost(): boolean {
    return this.isGroupOrder && this.$services.order.isCustomerHostOfGroupOrder();
  }

  get isGroupOrder(): boolean {
    return this.currentOrder?.order.orderType === OrderType.Group;
  }

  get participantMealCount(): number {
    const order = this.currentOrder;
    return order && order.order.meals ? getGroupOrderParticipantCount(order.order.meals) : 0;
  }

  get meals(): OrderMeal[] {
    const currentOrder = this.currentOrder;
    let meals = currentOrder?.order.meals || [];

    // filter out upsell items
    const upsellMeal = this.$appStore.state.order.upsellMeal;
    if (upsellMeal) {
      meals = meals.filter(meal => meal.mealId !== upsellMeal.mealId);
    }

    // sort group participant's meal to top for them
    if (this.isGroupOrder && !this.isGroupOrderHost && this.$appStore.state.order.groupOrderMealIds.length) {
      return meals.sort(sortFn(meal => this.$appStore.state.order.groupOrderMealIds.indexOf(meal.mealId) == -1));
    }

    return meals;
  }

  get orderNonFoodItems(): NonFoodItem[] {
    const currentOrder = this.currentOrder;
    return (currentOrder && currentOrder.order.nonFoodItems) || [];
  }

  get menuHasByofmBowls(): boolean {
    return this.$appStore.state.menu.activeMenu?.nonFoodItems?.some(nfi => nfi.itemId === BYOFM_BOWLS_MENU_ID) || false;
  } 

  get menuHasByofmServingUtensils(): boolean {
    return this.$appStore.state.menu.activeMenu?.nonFoodItems?.some(nfi => nfi.itemId === BYOFM_SERVING_UTENSILS_MENU_ID) || false;
  }

  get selectedRestaurant() {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get isDelivery(): boolean {
    return this.$services.order.isDelivery() && !!this.currentOrder?.order?.delivery;
  }

  get selectedDeliveryAddress() {
    return this.$appStore.state.order.selectedDeliveryAddress;
  }

  get isGroupParticipantWithEmptyBag(): boolean {
    return this.isGroupOrder && !this.isGroupOrderHost && !this.$services.order.groupParticpantHasMeal(this.$appStore.state.order.groupOrderMealIds);
  }

  get isPromoEntryAllowed() {
    // Allow the user to enter a promo they are below the cap
    return this.orderDiscounts.length < 1;
  }

  get isLoggedIn() {
    return this.$services.customer.isLoggedIn() === true;
  }

  get hasOnlyOneReward() {
    return this.availableRewards.length === 1;
  }

  get singleOrMultipleRewardsTitle() {
    return this.hasOnlyOneReward ? `USE ${this.availableRewards[0].promotionName}` : this.hasMultipleRewards ? `${this.availableRewards.length} ${this.multipleRewardsText}` : `NO ${this.multipleRewardsText}`;
  }

  get hasMultipleRewards() {
    return this.availableRewards.length > 1;
  }

  get availableRewards(): Promotion[] {
    if (this.orderIncludesDiscount) { return []; }

    // Get rewards that are not already applied
    return this.$appStore.state.customer.wallet.promotions.filter(p => !this.orderDiscounts.find(d => d.promotionCode == p.promotionCode));
  }

  get orderIncludesDiscount(): boolean {
    return any(this.orderDiscounts, d => this.isReward(d));
  }

  get pointsBalance(): number {
    return this.$services.reward.getPointsBalance();
  }

  get reward(): Promotion | null {
    return this.availableRewards[0] || null;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get isPaymentOnline(): boolean {
    return this.$services.payment.isAboveStorePaymentOnline();
  }

  get menuNonFoodItems(): MenuNonFoodItem[] {
    const { activeMenu } = this.$appStore.state.menu;
    const utensilsShow = this.$appStore.state.restaurant.selectedRestaurant?.utensilsShow;
    let menuSet = sortMenuNonFoodItems(activeMenu?.nonFoodItems) || [];

    return menuSet.filter(m => (utensilsShow && m.itemId === NAPKINS_AND_UTENSILS_MENU_ID) || ((m.itemId === BYOFM_BOWLS_MENU_ID || m.itemId === BYOFM_SERVING_UTENSILS_MENU_ID) && this.orderContainsBYOFMProtein()));
  }

  get groupParticipantText(): string {
    return this.isGroupParticipantWithEmptyBag ? 'Add Your Meal' : 'Add Another Menu Item';
  }

  get dancingVeggiesImageUrl() {
    return this.$appStore.state.cms.images.dancingVeggies.default;
  }

  get promotionName() {
    return this.currentOrder?.discounts?.[0]?.promotionName || this.currentOrder?.order.discounts?.[0].discountName || this.reward?.promotionName || this.availableRewards[0].promotionName || 'Free Entree'
  }

  get happyAvocadoUrl() {
    return this.$appStore.state.cms.images.happyAvocado.default;
  }

  get applyReward(){
    return this.$appStore.state.cms.images.icons.rewardArrow;
  }
  
  get sadAvocadoUrl() {
    return this.$appStore.state.cms.images.lonelyAvocado.default;
  }

  get refreshIcon() {
    return this.$appStore.state.cms.images.icons.refreshGold.default;
  }

  get groupParticipantMessage(): string {
    return this.isGroupParticipantWithEmptyBag ? `add your meal to the order` : `you're all set`;
  }

  get discountErrorMessage(): string {
    switch (this.discountErrors) {
      case OrderErrorCode.PromotionCodeInvalidLocation: return 'Invalid Promo Code';
      case OrderErrorCode.PromotionPickUpOnly:
        this.setPromoErrorMessage('Promo code is for pickup only');
        this.promoCode.dirty = true;
        return this.promoCode.error = this.promoErrorMessage;
      default: return 'Missing Item';
    }
  }

  get groupParticipantSubMessage(): string {
    return this.isGroupParticipantWithEmptyBag
      ? `You haven't added a meal yet.`
      : `Add more if you like. ${this.$appStore.state.order.pendingGroupOrder?.order.customerFirstName} will take care of the rest.`;
  }

  get rewardAddedText() {
    return this.currentOrder?.discounts?.[0].promotionName.toUpperCase();
  }

  get displayContextualReward(): boolean {
    return (
      this.$services.loyalty.isLoyaltyEligible(this.$appStore.state.customer.profile) &&
      !this.orderIncludesDiscount &&
      this.$appStore.state.reward.bagRewards.length > 0
    );
  }

  get singleOrMultipleContextualBagOffersText(): string {
    const rewards = this.$appStore.state.reward.bagRewards;
    switch(rewards.length) {
      case 0: return '';
      case 1: return `${this.rewardTitle} | ${this.bagRewardPoints} PTS`;
      default: return 'REDEEM YOUR POINTS';
    }
  } 

  get rewardTitle() {
    const rewards = this.$appStore.state.reward.bagRewards;
    switch(rewards.length) {
      case 0: return '';
      case 1: return `Free ${rewards[0].title}`;
      default: return 'Reward';
    }
  }

  get bagRewardPoints(): string {
    const rewards = this.$appStore.state.reward.bagRewards;
    return rewards.length == 0 ? '' : rewards[0].points.toLocaleString();
  }

  get showRewardsConfirmation(): boolean {
    const rewardAdded = this.$appStore.state.ui.bag.rewardConfirmation;
    if (rewardAdded) { this.contextualRewardUsed = true; }
    return rewardAdded;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get signInSignUp() {
    return 'Sign In To Use Rewards';
  }

  // FullStory analytics attribute
  get dataAnalyticsType(): {[dataAnalyticsAttribute: string]: string} {
    return {[`data-analytics-${this.$appStore.state.ui.bag.dataAnalyticsType}`]: 'bag'};
  }

  get bagOptions() {
    return countries[this.$appStore.state.ui.countryProfile.countryCode.toLocaleLowerCase()].bagOptions;
  }

  get isRewardsOnline(): boolean{
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus){
      return flags.orderingStatus.isRewardsOnline;
    }

    return this.bagOptions.isRewardsOnline;
  }

  get multipleRewardsText(): string {
    return this.bagContent?.multiplePromosAvailable || 'REWARDS AVAILABLE';
  }

  get emptyBagRewardAddedText(): string {
    return this.bagContent?.promoAddedToEmptyBag || 'REWARD ADDED';
  }

  get legalText(): string {
    // This CF property wasn't originally meant for this, but it works. Can circle back post MVP
    return this.bagContent?.viewPromoModalDisclaimer || 'Limit one reward or promo per order.';
  }

  get promoName(): string {
    // This CF property wasn't originally meant for this, but it works. Can circle back post MVP
    return this.bagContent?.viewPromoModal.toLowerCase() || 'rewards';
  }

  get useMedallionStyling(): boolean {
    return this.bagOptions.useRewardsMedallionStyling;
  }

  displayRewardConfirmation() {
    this.$services.ui.setRewardConfirmationVisibility(true);
  }

  getDisplayInputType(itemId: string): string {
    return this.$services.metadata.getDisplayInputType(itemId);
  }
  
  get customerDeselectedByofmBowls(): boolean {
    return this.$appStore.state.order.customerDeselectedByofmBowls;
  }

  get customerSelectedByofmServingUtensils(): boolean {
    return this.$appStore.state.order.customerSelectedByofmServingUtensils;
  }

  get utensilsMenuItem(): MenuNonFoodItem | undefined {
    return this.menuNonFoodItems.find(item => item.itemId === NAPKINS_AND_UTENSILS_MENU_ID);  
  }

  get byofmBowlsMenuItem(): MenuNonFoodItem | undefined {
    return this.menuNonFoodItems.find(item => item.itemId === BYOFM_BOWLS_MENU_ID);
  }

  orderContainsBYOFMProtein(): boolean | undefined {
    return this.currentOrder?.order.meals?.some((meal) => isMealBYOProtein(meal, this.$appStore.state.menu.activeMenu));
  }

  @Watch('currentOrder', { immediate: true })
  onOrderChanged() {
    // Refresh non-food items whenever the order changes to ensure the checkboxes reflect the current order state
    this.displayedNonFoodItems = [];
    this.menuNonFoodItems.forEach(menuItem => { 
      this.displayedNonFoodItems.push({item: menuItem, checked: this.includedInOrder(menuItem.itemId)});
    });
  }

  async mounted() {
    // Delaying page load event in order to be sure the upsell impression
    // data has been processed
    setTimeout(() => {
      this.$services.analytics.send(new BagPageLoadEvent());
    }, 350);

    await this.getBagContent();
    await this.refreshGroupOrder();
    this.checkForExpiredDonation();
    this.getBagOffers();
    this.checkForEPops();

    let order = this.currentOrder;

    this.setNonFoodItemsState();

    if (!order || !order.order.discounts || order.order.discounts.length > 1) {
      return;
    }
    
    const promoCode = order.order.discounts[0].discountCode;
    this.$services.order.setPromoCode(promoCode);
    await this.processOrderDiscountErrors(order, promoCode);
  }

  destroyed() {
    // reset data analytics type whenever bag closes.
    this.$services.bag.setDataAnalyticsType();
  }

  async checkForEPops() {
    const epops = await this.$services.epop.getEpopsForRestaurant(this.selectedRestaurant!.restaurantNumber);
    if (epops && epops.length > 0) {
      this.$services.ui.setModalVisibility('epop', true, {
        epops,
      });
      this.$services.ui.firstFocus();
    }
  }

  async checkForExpiredDonation() {
    if (this.currentOrder?.order.donations && this.currentOrder?.order.donations.length) {
      let charitableCampaign = await this.$services.donations.getCharitableCampaign(this.countryCode);
      if (charitableCampaign?.campaignId != this.currentOrder.order.donations[0].charitableCampaignId) {
        try {
          await this.$services.order.deleteDonation(this.currentOrder, this.currentOrder.order.donations[0].charitableCampaignId);
        } catch (error: any) {
          this.$services.logger.error('Could not remove expired campaign donation.', error!);
        }
      }
    }
  }

  get rewardToastVisible() {
    return this.$appStore.state.ui.bag.bagToastVisibility;
  }

  displayRewardToast(visible: boolean) {
    this.$services.ui.setBagRewardsToastVisible(visible);
  }

  private handleAddToBagException(e: any) {
    if (!(e instanceof OrderApiException)) {
      throw e;
    }

    const error = e.errors[0];
    if (!error) {
      throw e;
    }

    switch (error.errorCode) {
      case OrderErrorCode.RewardExceedsLimit:
        this.$services.ui.setModalVisibility('toast', true, {
          title: '',
          body: 'Only one reward per order.',
          bodyOnly: true,
        });
        Vue.nextTick(() => {
          setTimeout(() => {
            this.$services.ui.nextFocus('reward-heading');
          }, 6000);
        });
        break;
    }
  }

  async processSingleReward(reward: Promotion) {
   this.$services.ui.setLastSelectedElement('bag');
   this.$services.ui.setAppPepperVisibility(true);

   this.$services.analytics.send(new AccountManagementEvent(InteractionTrackingType.RewardsAddedToBag, undefined, reward.promotionName, 'Bag'));

   let order = this.$services.order.getCurrentOrder();
   const promotionCode = reward.promotionCode;

  if (!order) {
   if (this.$appStore.state.restaurant.selectedRestaurant) {
    order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant!.restaurantNumber);
   } else {
     
    this.$services.ui.setModalVisibility('findAChipotle', true, {
      onClose: async () => {
        if (!order || !this.$appStore.state.restaurant.selectedRestaurant) {
          return;
        }

        try {
          order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant.restaurantNumber,);

          if (order && promotionCode) {
            await this.$services.order.applyDiscountToOrder(order, promotionCode);

            this.$services.bag.setBagVisibility(true);
            this.displayRewardToast(true);
          }
        } catch (e: any) {
          this.handleAddToBagException(e);
        }
      },
    });

    this.$services.ui.setModalVisibility('toast', true, {
      body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
      bodyOnly: true,
    });
    Vue.nextTick(() => this.$services.ui.focusFirstDiv('.cmg-modal-container'));
    return;
    }
  }

    if (!order) {
      return;
    }
  try {
    if (!order.discounts || !order.discounts.find(d => d.promotionCode === promotionCode)) {

      await this.$services.order.applyDiscountToOrder(order, promotionCode);
      
      this.$nextTick(() => {
        Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
        this.$services.ui.setLastSelectedElement('bag');
      });
    } else {
      await this.$services.order.removeDiscountFromOrder(order, promotionCode);
      
      Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
    }
    
  } catch (e: any) {
    this.handleAddToBagException(e);
  } finally {
    this.$services.ui.setAppPepperVisibility(false);
    this.displayRewardToast(true);
    this.$emit('rewardAdded');
    }
  }
  
  onClickUseRewards() {
    if (this.hasMultipleRewards) {
      this.$services.ui.setModalVisibility('rewardsListModal', true, {
        rewards: this.availableRewards
      });
    } 
    else {
      this.processSingleReward(this.availableRewards[0]);
    }

    if (this.hasDiscounts) {
      this.displayRewardToast(true);
    }
  }

  async refreshGroupOrder() {
    if (this.currentOrder?.order.orderType != OrderType.Group) {
      return;
    }

    try {
      // Show loading pepper for GO participant refresh feedback
      this.$services.ui.incrementLoader('bag');
      await this.$services.order.refreshCurrentOrder(true);
    } catch {
      // TODO-ms5dkw: real ux to handle error
      this.$services.ui.setModalVisibility('globalErrorToast', true);
    } finally {
      this.$services.ui.decrementLoader('bag');
    }
  }

  async getBagOffers() {
    if (!this.isOrderEligibleForContextualRewards()) { return; }
    this.$services.reward.getBagOffers();
  }

  onClickStartOrder() {
    this.navigateToMenu();
    this.$services.ui.clearLastSelectedElement();
  }

  async onClickInviteOthers(event: any): Promise<void> {
    this.$services.ui.incrementLoader('bag');

    try {
      if (await this.$services.order.doesInProgressHostGroupOrderExist()) {
        this.$services.ui.setModalVisibility('cancelGroupOrder', true);
      } else {
        this.$router.push({ name: Routes.GroupOrderCreate });
        this.$services.ui.nextFocus('create-group-order');
      }

      this.$services.bag.setBagVisibility(false);
    } finally {
      this.$services.ui.decrementLoader('bag');
    }
  }

  onClickCloseBag() {
    this.closeBag();
  }

  onClose() {
    this.$services.bag.setBagVisibility(false);
  }

  onClickCheckout(event: any) {
    if (this.contextualRewardUsed) {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.ContextualBag,
        action: AnalyticsEventAction.ContextRewardCheckoutWithRewardClick,
        label: AnalyticsEventLabel.ContextRewardCheckoutWithRewardClick,
      });
    } else {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.ContextualBag,
        action: AnalyticsEventAction.ContextRewardCheckoutWithNoRewardClick,
        label: AnalyticsEventLabel.ContextRewardCheckoutWithNoRewardClick,
      });
    }
    
    const unappliedDiscounts = this.orderDiscounts.filter(d => this.discountIsMissingItem(d));

    if (unappliedDiscounts.length) {
      this.$services.ui.pushEventTargetAsLastSelectedElement(event);
      this.$services.ui.setModalVisibility('basicError', true, {
        header: 'Heads Up',
        content: `Your ${joinWithSuffix(
          unappliedDiscounts.map(d => d.promotionName),
          ', ',
          ' & ',
        )} ${unappliedDiscounts.length == 1 ? 'has' : 'have'} not been applied because a qualifying item is missing from your order. Do you want to continue?`,
        options: [
          {
            text: 'Cancel',
            type: ButtonType.Secondary,
            onClick: () => this.$services.ui.setModalVisibility('basicError', false),
          },
          {
            text: 'Check Out',
            type: ButtonType.Primary,
            onClick: async () => {
              const unappliedDiscounts = this.orderDiscounts.filter(d => this.discountIsMissingItem(d));

              if (this.currentOrder) {
                for (const discount of unappliedDiscounts) {
                  await this.$services.order.removeDiscountFromOrder(this.currentOrder, discount.promotionCode);
                }
              }

              this.$services.ui.setModalVisibility('basicError', false);
              this.checkout();
            },
          },
        ],
      });
      this.$services.ui.firstFocus();
      return;
    }

    this.checkout();
  }

  onGroupParticipantAddMenuClick() {
    this.navigateToMenu(true);
  }

  onGroupParticipantRefreshClick() {
    this.refreshGroupOrder();
  }

  onClickLeaveGroupOrder() {
    this.$services.ui.setModalVisibility('leaveGroupOrder', true);
  }

  onClickGetReward() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.ContextualBag,
      action: AnalyticsEventAction.ContextRewardGetClick,
      label: AnalyticsEventLabel.ContextRewardGetClick,
    });
    this.$services.ui.setModalVisibility('bagOffer', true);
  }

  onRequestMealEdit(meal: OrderMeal) {
    this.mealBeingEdited = meal;
  }

  async onMealEditComplete() {
    this.mealBeingEdited = null;

    const body = this.$refs['body'] as HTMLElement;
    body.style.overflowY = 'unset';

    if (this.editMealRepositionHandler) {
      window.removeEventListener('resize', this.editMealRepositionHandler);
    }
  }

  formatTotal(total: number | null): string {
    return this.$services.utils.formatPrice(total);
  }

  showFindAChipotleModal() {
    this.$services.ui.setModalVisibility('findAChipotle', true);
    this.$nextTick(() => this.$services.ui.focusDiv('.cmg-modal-container'));
    this.closeBag();
    this.$services.ui.setLastSelectedElement('bag');

    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.Behavior,
      action: AnalyticsEventAction.Click,
      label: AnalyticsEventLabel.BagPickupDelivery,
    });
  }

  async onClickApplyReward() {
    const reward = this.reward;
    const order = this.currentOrder;
    if (!order || !reward) {
      return;
    }

    const promoCode = reward.promotionCode;

    this.rewardLoading = true;

    try {
      if ((order.order.discounts || []).find(p => p.discountCode == promoCode)) {
        return;
      }

      const promotion = await this.$services.promotions.getPromotionDetails(promoCode);
      if (!promotion) {
        return;
      }

      if (moment(promotion.expirationDate).isBefore(moment(), 'day')) {
        this.$services.ui.setModalVisibility('basicError', true, {
          header: null,
          content: 'The reward you selected has expired',
          options: [{ text: 'Okay', type: ButtonType.Primary, onClick: () => this.$services.ui.setModalVisibility('basicError', false) }],
        });

        return;
      }

      try {
        await this.$services.order.applyDiscountToOrder(order, promotion.promotionCode);
      } catch (e: any) {
        // TODO-78eunz: handle errors
        throw e;
      }
    } catch (e: any) {
      this.$services.ui.setModalVisibility('globalErrorToast', true);
      this.$services.logger.error('onClickApplyReward: ', e!);
    } finally {
      this.rewardLoading = false;
    }
  }

  async onClickApplyPromoCode(event: any) {
    this.setPromoErrorMessage('');
    const order = this.currentOrder;
    if (!order) {
      return;
    }

    const promoCode = this.promoCode.value;
    if (!promoCode) {
      this.promoCode.error = 'Invalid Promo Code';
      return;
    }

    this.promoLoading = true;

    this.promoCode.dirty = true;
    this.promoCode.errorMode = ErrorMode.PlaceHolder;

    try {
      if ((order.order.discounts || []).find(p => p.discountCode == promoCode)) {
        this.promoCode.error = 'Promo Code Already Applied';
        return;
      }
      const promotion = await this.$services.promotions.getPromotionDetails(promoCode);

      if (promotion?.isReusable && order.order.discounts && (order.order.discounts[0] as any).discountIsReusable) {
        this.promoCode.error = 'Only One Reusable Promo Allowed';
        return;
      }

      let isValid = promotion && promotion.isValid;
      if (promotion && moment(promotion.expirationDate).isBefore(moment(), 'day')) {
        this.promoCode.error = 'Promo code has expired';
        isValid = false;
        return;
      }

      if (!isValid) {
        if (promotion?.invalidReason?.toLowerCase() === 'redeemed') {
          this.$services.ui.pushEventTargetAsLastSelectedElement(event);

          this.$services.ui.setModalVisibility('basicError', true, {
            header: 'Oh Bummer.',
            content: `Looks like this promo code is all used up and no longer valid.`,
            options: [
              {
                text: 'Okay',
                type: ButtonType.Primary,
                onClick: () => {
                  this.$services.ui.setModalVisibility('basicError', false);
                  this.promoCode.value = '';
                },
              },
            ],
          });
        } else if (promotion?.invalidReason?.toLowerCase() === 'inactive') {
          this.promoCode.error = 'Offer is not available for redemption yet';
        } else {
          this.promoCode.error = 'Invalid Promo Code';
        }

        return;
      }

      try {
        if (promotion) {
          this.$services.order.setPromoCode(promotion.promotionCode);
          await this.$services.order.applyDiscountToOrder(order, promotion.promotionCode, promotion);
          await this.$services.order.throwIfDiscountErrorCode(OrderErrorCode.PromotionCodeLocked);
        }
      } catch (e: any) {
        if (e instanceof OrderApiException) {
          const exception = e as OrderApiException;
          const error = exception.errors[0];

          switch (error.errorCode) {
            case OrderErrorCode.RewardExceedsLimit:
              this.promoCode.error = 'Only one reward per order';
              return;
            case OrderErrorCode.PromotionPickUpOnly:
              this.promoCode.error = 'Promo code is for pickup only';
              this.promoCode.dirty = true;
              this.setPromoErrorMessage(this.promoCode.error);
              return;
            case OrderErrorCode.PromotionCodeLocked:
              this.$services.ui.setModalVisibility('basicError', true, {
                      header: 'Aw Shucks',
                      content: `<p>Looks like that promo is already in use. You can continue without it or try another code.</p><br/><span class='promo-missing-item-text'>${promoCode}</span>`,
                      options: [
                        {
                          text: 'Okay',
                          type: ButtonType.Primary,
                          onClick: () => {
                            this.$services.ui.setModalVisibility('basicError', false);
                          },
                        },
                      ],
                    });
              return;
          }
        }

        throw e;
      }

      this.promoCode.error = this.promoErrorMessage || '';
      this.promoCode.value = '';
    } catch (e: any) {
      const error = e as PromotionsApiError;
      if (!error || !error.errorCode) {
        throw e;
      }

      switch (error.errorCode) {
        case PromotionsApiErrorCode.PromoNotFound:
          this.promoCode.error = 'Code does not exist';
          this.promoCode.errorMode = ErrorMode.PlaceHolder;
          break;
      }
    } finally {
      this.promoLoading = false;
      this.processOrderDiscountErrors(order, promoCode);
    }

  }

  onUpdatePromoCode() {
    this.promoCode.error = '';
    this.promoCode.dirty = false;
    this.setPromoErrorMessage('');
  }

  getDiscountValueDisplay(promo: Promotion) {
    return '- ' + this.formatTotal(this.getDiscountAmount(promo));
  }

  getDiscountAmount(promo: Promotion) {
    if (!this.currentOrder) {
      return null;
    }

    const discount = (this.currentOrder.order.discounts || []).find(d => d.discountCode == promo.promotionCode);
    return discount ? discount.discountAmount : null;
  }

  async onClickRemovePromo(promo: Promotion) {
    if (!this.currentOrder || this.promoBeingRemoved) {
      return;
    }
    this.promoCode.value = '';
    this.setPromoErrorMessage('');
    if (this.contextualRewardUsed) {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.ContextualBag,
        action: AnalyticsEventAction.ContextRewardRemoveClick,
        label: AnalyticsEventLabel.ContextRewardRemoveClick,
      });
    }

    try {
      this.promoBeingRemoved = promo;
      await this.$services.order.removeDiscountFromOrder(this.currentOrder, promo.promotionCode);
      this.contextualRewardUsed = false;
      this.$services.ui.setRewardConfirmationVisibility(false);
     
    } finally {
      this.setPromoErrorMessage('');
      this.promoCode.dirty = false;
      this.promoBeingRemoved = null;
    }
  }

  onClickSignIn(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.utils.setIsFromBag(true);

    this.$services.ui.setModalVisibility('signIn', true);

    this.$services.ui.firstFocus();
  }

  discountIsMissingItem(promo: Promotion) {
    const isFundraiser = promo.promotionGroup !== null && (promo.promotionGroup.toLowerCase() === 'tracking' || promo.promotionGroup.toLowerCase().includes('trackingemail'));
    return this.getDiscountAmount(promo) == 0 && !isFundraiser;
  }

  setPromoErrorMessage(message: string) {
    this.promoCode.dirty = !!message
    this.$services.order.setPromoErrorMessage(message);
  }

  async processOrderDiscountErrors(order: OrderWithContext, promoCode: string) {
    await this.$services.order.processOrderDiscountErrors(order, promoCode);
    if (this.$appStore.state.order.promoErrorMessage) {
      this.promoCode.dirty = true;
      this.promoCode.value = this.$appStore.state.order.promoCode;
      this.promoCode.error = this.$appStore.state.order.promoErrorMessage;
    }
  }

  onClickGoToRewards() {
    this.$router.push({ name: Routes.Rewards });
    this.closeBag();
    this.$services.ui.clearLastSelectedElement();
  }

  onClickBackground() {
    this.closeBag();
  }

  onClickAddAnotherMenuItem() {
    this.$services.analytics.send(new BagClickEvent('addAnotherMenuItem', this.meals));
    this.navigateToMenu();
  }

  updateUpsellLoading(isLoading: boolean) {
    this.shouldShowOverlay = isLoading;
  }
  
  onMealRemoved() {
    setTimeout(() => {
      (this.$refs['meal-cards'] as any[])[0].$refs['entrees-container'].focus();
    }, 1500);
  }

  onTouchStart(event: TouchEvent) {
    this.startY = event.touches[0].clientY;
    this.bagBodyScrollTop = (this.$refs['body'] as HTMLElement).scrollTop;
  }

  onTouchMove(event: TouchEvent) {
    const currentY = event.touches[0].clientY;
    const delta = currentY - this.startY;

    (this.$refs['body'] as HTMLElement).scrollTop = this.bagBodyScrollTop - delta;
  }

  async toggleNonFoodItemRadioButton(itemId: string) {
    const menuItem = this.menuNonFoodItems.find(i => i.itemId == itemId);
    const orderItem = this.orderNonFoodItems.find(i => i.menuItemId == itemId);
    this.$services.ui.incrementLoader('bag');

    try {
      if (this.currentOrder && menuItem) {
        if (orderItem) {
          await this.removeNonFoodItemFromOrder(orderItem.nonFoodItemId);   
          this.toggleNonFoodItemSelections(itemId, false);
        } else {
          await this.addNonFoodItemToOrder(menuItem.itemId);
          this.toggleNonFoodItemSelections(itemId, true);
        }
      }
    } catch (error: any) {
      this.$services.logger.error('Something went wrong with toggleNonFoodItemRadioButton: ', error!);
    } finally {
      this.$services.ui.decrementLoader('bag');
    }

    this.logAnalytics(itemId, orderItem);
  }

  includedInOrder(itemId: string): boolean {
    return this.orderNonFoodItems.some(i => i.menuItemId == itemId);
  }

  toggleNonFoodItemSelections(itemId: string, addedNonFooditem: boolean) {
    if (itemId === BYOFM_SERVING_UTENSILS_MENU_ID) {
        this.$services.order.setCustomerSelectedServingUtensils(addedNonFooditem);
      }
      if (itemId === BYOFM_BOWLS_MENU_ID) {
        this.$services.order.setCustomerDeselectedByofmBowls(!addedNonFooditem);
      }
  }

  private async addNonFoodItemToOrder(nonFoodItemId: string) {
    if (this.currentOrder) {
      await this.$services.order.addNonFoodItemToOrder(this.currentOrder, { menuItemId: nonFoodItemId, quantity: 1, isUpSell: false });
      
      const item = this.displayedNonFoodItems.find(i => i.item.itemId === nonFoodItemId);
      if (item) {
        item.checked = true;
      }
    }
  }

  private async removeNonFoodItemFromOrder(nonFoodItemId: string) {
    if (this.currentOrder) {
      await this.$services.order.removeNonFoodItemFromOrder(this.currentOrder, nonFoodItemId);

      const item = this.displayedNonFoodItems.find(i => i.item.itemId === nonFoodItemId);
      if (item) {
        item.checked = false;
      }
    }
  }

  private async setNonFoodItemsState() {
    if (this.currentOrder && orderContainsBYOProtein(this.currentOrder, this.$appStore.state.menu.activeMenu)) {
      if (this.menuHasByofmBowls && !this.includedInOrder(BYOFM_BOWLS_MENU_ID) && !this.customerDeselectedByofmBowls) {
        try {
          await this.addNonFoodItemToOrder(BYOFM_BOWLS_MENU_ID);
        } catch (error: any) {
          this.$services.logger.error('Error adding BYOFM bowls to order', error);
        }
      }
      if (this.menuHasByofmServingUtensils && !this.includedInOrder(BYOFM_SERVING_UTENSILS_MENU_ID) && this.customerSelectedByofmServingUtensils) {
        try {          
          await this.addNonFoodItemToOrder(BYOFM_SERVING_UTENSILS_MENU_ID);
        } catch (error: any) {
          this.$services.logger.error('Error adding BYOFM serving utensils to order', error);
        }
      }
    }
  }

  private navigateToMenu(isGroupOrder: boolean = false) {
    const path = isGroupOrder ? '/order/group/menu' : '/#menu';

    // If already on homepage menu or group menu or nutrition calculator, stay on the page
    if (this.$route.fullPath == path || this.$route.fullPath === '/nutrition-calculator') {
      this.$services.ui.setShouldScrollHomeToHash(true);
      this.$services.ui.clearLastSelectedElement();
    // Else, navigate to the homepage menu
    } else {
      if (isGroupOrder) {
        this.$router.push({ name: Routes.GroupOrderMenu });
      } else if ( this.$route.fullPath.indexOf('/nutrition-calculator') === 0) {
        this.$router.push({ name: Routes.NutritionCalculatorPage });
      } else {
        this.$router.push({ name: Routes.Order, hash: '#menu' });
      }
    }
    
    const focusFirstelement = document.querySelector('.top-level-menus .top-level-menu .content .text .display-name') as HTMLElement;
    if (focusFirstelement) {
      focusFirstelement.focus();
    }
    this.$services.ui.clearLastSelectedElement();
    this.closeBag();
  }

  private isReward(discount: Promotion): boolean {
    return !discount.isReusable;
  }

  private closeBag() {
    this.$services.bag.setBagVisibility(false);
    this.$services.ui.focusBack();
  }

  private isOrderEligibleForContextualRewards(): boolean {
    return this.isLoggedIn && this.isEnrolledInLoyalty && this.pointsBalance >= CONTEXTUAL_BAG_POINTS_MINIMUM;
  }

  private getStylesDescriptor(): StylesDescriptor {
    return {
      empty: !this.hasMeals,
    };
  }

  private checkout() {
    this.closeBag();

    if (this.currentOrder) {
      this.$services.googleAnalytics.sendEcommerceCheckoutEvent(this.currentOrder, 'initiate checkout', 1);
    }

    this.$router.push({ name: Routes.Checkout });
  }

  private logAnalytics(itemId: string, orderItem: NonFoodItem | undefined) {
    if (itemId == NAPKINS_AND_UTENSILS_MENU_ID) {
      const category = AnalyticsEventCategory.ECommerce;
      const action = AnalyticsEventAction.AddToBagNonFoodItem;
      const label = orderItem ? AnalyticsEventLabel.NapkinsUtensilsUnSelected : AnalyticsEventLabel.NapkinsUtensilsSelected;

      this.$services.googleAnalytics.sendCustomEvent({ category, action, label });
    }
  }

  private async getBagContent() {
    let bagContent = this.$services.config.getBagContent();
    if (bagContent) {
      this.bagContent = bagContent;
    } else {
      await this.$services.config.setBagContent();
      this.bagContent = this.$services.config.getBagContent()!;
    }
  }
}
